/**
 * FireParticles
 * ─────────────
 * Subtle canvas-based ember layer that drifts up from the bottom edges
 * of the viewport. Particles are drawn with additive blending and a
 * radial gradient per particle so they read as soft glowing embers,
 * not as a tracked-out video filter.
 *
 * Design choices:
 *   - Canvas (not DOM nodes) so 60 particles ~= 0.1 ms / frame on a
 *     mid-tier laptop. requestAnimationFrame, pauses on tab-hidden.
 *   - Particle density biased toward the bottom 35 % of the viewport
 *     (~55 / 45 split) so the ember pool feels strongest near the
 *     footer without any explicit "stronger here" code path.
 *   - Lighter blending (`lighter`) over a transparent canvas — the
 *     existing animated-bg radial blobs show through cleanly.
 *   - Respects prefers-reduced-motion: no particles at all if set.
 */
import React, { useEffect, useRef } from 'react';

export default function FireParticles({ density = 1 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    // Wait until the portal has actually mounted the canvas — on the
    // first render `portalTarget` is null and the component returns
    // null, so canvasRef stays null. This effect re-runs once
    // portalTarget flips to document.body and the canvas exists.
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });

    let width = 0;
    let height = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Mobile detection — drives count, spawn shape, and edge logic.
    // Recomputed at resize-time so device-rotation that crosses the
    // breakpoint adjusts the ember field on the fly.
    let isMobile = window.matchMedia('(max-width: 767px)').matches;

    // Drive the canvas off its OWN computed size, not window.innerHeight.
    // Combined with `height: 100lvh` in the JSX style below, this means
    // the canvas covers the LARGE viewport (= viewport without the
    // address bar) at all times. On iOS Safari that fixes the dark
    // ~50 px strip that flashed at the bottom every time the address
    // bar collapsed during scroll — the canvas now stays the same
    // height across both address-bar states instead of resizing a
    // frame late.
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      // Cold-start guard — on first paint the rect can be 0×0 before
      // layout settles; fall back to window size so embers spawn into
      // a real area instead of a zero-size canvas.
      width  = Math.round(rect.width)  || window.innerWidth  || 0;
      height = Math.round(rect.height) || window.innerHeight || 0;
      isMobile = window.matchMedia('(max-width: 767px)').matches;
      canvas.width  = Math.floor(width  * dpr);
      canvas.height = Math.floor(height * dpr);
      // No inline style writes — JSX-level CSS handles display size.
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);
    // Orientation flips on iOS don't always emit a clean 'resize'
    // event — visualViewport does. Hook it too where available.
    window.visualViewport?.addEventListener('resize', resize);

    // Half the count on phones — fewer GPU paints per frame, and the
    // smaller viewport doesn't need 60 embers to feel populated anyway.
    const PARTICLE_COUNT = Math.round((isMobile ? 26 : 60) * density);
    const particles = [];

    // Spawn a fresh ember. `initial=true` distributes the FIRST batch
    // across the bottom band so the canvas isn't empty on mount but
    // also doesn't paint embers all the way up to the top of the page.
    const spawn = (initial = false) => {
      // Bottom-bias: 100 % bottom on mobile (edge spawns at x<60 px
      // land mid-screen on a 414 px viewport — looks like embers
      // appearing in the centre, not the sides). 65 % bottom on
      // desktop, the rest from the left/right edges for asymmetry.
      const fromBottom = isMobile ? true : Math.random() < 0.65;
      let x, y;
      if (fromBottom) {
        x = Math.random() * width;
        if (initial) {
          // First paint: seed the bottom 50 % of the viewport (was
          // the FULL height — that's what was making mobile look
          // like 'embers everywhere' instead of 'embers from below').
          y = height * 0.5 + Math.random() * height * 0.5;
        } else {
          y = height + Math.random() * 40;
        }
      } else {
        // Edge spawn — desktop only (gated above via fromBottom).
        const fromLeft = Math.random() < 0.5;
        x = fromLeft ? Math.random() * 60 : width - Math.random() * 60;
        y = height * (0.5 + Math.random() * 0.45);
      }

      return {
        x,
        y,
        vx: (Math.random() - 0.5) * 0.35,
        vy: -(Math.random() * 0.45 + 0.18),
        size: Math.random() * 2 + 0.6,
        opacity: 0,
        peak: Math.random() * 0.55 + 0.2,
        life: 0,
        maxLife: Math.random() * 360 + 240,
        // Slight hue range around the brand orange (#F97316 ≈ hue 22)
        hue: 18 + Math.random() * 14,
      };
    };

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push(spawn(true));
    }

    let rafId = 0;
    let lastFrame = performance.now();

    const render = (now) => {
      const dt = now - lastFrame;
      lastFrame = now;
      // Frame-time scaled to ~60 fps so motion stays consistent across
      // refresh rates.
      const dtScale = Math.min(dt / 16.67, 2.5);

      ctx.clearRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        p.x += p.vx * dtScale;
        p.y += p.vy * dtScale;
        p.life += dtScale;
        // Tiny flicker on the horizontal drift so embers don't move
        // in perfectly straight lines.
        p.vx += (Math.random() - 0.5) * 0.012;

        // Fade in over first 60 ticks, full opacity in the middle,
        // fade out across the final 110 ticks.
        if (p.life < 60) {
          p.opacity = (p.life / 60) * p.peak;
        } else if (p.life > p.maxLife - 110) {
          p.opacity = Math.max(0, ((p.maxLife - p.life) / 110) * p.peak);
        } else {
          p.opacity = p.peak;
        }

        const dead =
          p.life > p.maxLife ||
          p.y < -20 ||
          p.x < -20 ||
          p.x > width + 20;
        if (dead) {
          particles[i] = spawn();
          continue;
        }

        // Soft radial gradient: bright orange core → transparent edge.
        const r = p.size * 5;
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
        grad.addColorStop(0, `hsla(${p.hue}, 100%, 65%, ${p.opacity})`);
        grad.addColorStop(0.35, `hsla(${p.hue}, 100%, 52%, ${p.opacity * 0.55})`);
        grad.addColorStop(1, `hsla(${p.hue}, 100%, 50%, 0)`);

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Soft bottom-edge glow — independent of particles so it doesn't
      // get eaten when a particle drifts off. Sits below the embers
      // visually because we paint it first.
      const bottomGrad = ctx.createLinearGradient(0, height, 0, height * 0.65);
      bottomGrad.addColorStop(0, 'rgba(249, 115, 22, 0.10)');
      bottomGrad.addColorStop(1, 'rgba(249, 115, 22, 0)');
      ctx.fillStyle = bottomGrad;
      ctx.fillRect(0, height * 0.65, width, height * 0.35);

      ctx.globalCompositeOperation = 'source-over';
      rafId = requestAnimationFrame(render);
    };
    rafId = requestAnimationFrame(render);

    // Stop the loop when the tab is hidden — saves a lot of battery
    // when the user is in another tab.
    const onVis = () => {
      if (document.hidden) {
        cancelAnimationFrame(rafId);
      } else {
        lastFrame = performance.now();
        rafId = requestAnimationFrame(render);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      window.visualViewport?.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none"
      // height: 100lvh = large viewport height; on iOS Safari this is
      // the viewport WITHOUT the address bar, so the canvas stays the
      // same height regardless of whether the bar is currently visible
      // or collapsed. Fallback to 100vh for older browsers. width is
      // 100vw both ways. zIndex 0 keeps it behind everything else.
      //
      // translate3d(0,0,0) (and the WebKit prefix for older Safari)
      // forces the canvas onto its own GPU compositing layer. That
      // pins it harder to the viewport on iOS during the address-bar
      // collapse animation — without the explicit layer hint, Safari
      // briefly lets fixed elements scroll along with the page for
      // the first ~50 px of that animation. backfaceVisibility:hidden
      // is the matching old-Safari toggle to keep the layer flat.
      style={{
        zIndex: 0,
        width: '100vw',
        height: '100lvh',
        transform: 'translate3d(0, 0, 0)',
        WebkitTransform: 'translate3d(0, 0, 0)',
        WebkitBackfaceVisibility: 'hidden',
      }}
    />
  );
}
